import { css, cva } from '@styled-system/css';
import { Package, Pause, Play, Radar, Store, Trash2 } from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { App, message as messageStatic } from 'antd';

import { SearchModal, type SearchModalInput } from '@/features/productSearch/components/SearchModal';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';

import { useCreateVeille, useUserVeilles, useVeilleLifecycle, useVeilleQuotas } from '../useVeilleApi';
import { CreateVendorVeilleModal } from './CreateVendorVeilleModal';
import { liveDot, pausedBadge } from './veilleUi';

// ============================================================================
// Page Veille — branchée sur l'API réelle : getUserVeilles + quotas serveur,
// création par critères (popup produit) ou vendeur (résolution de pseudo),
// pause/reprise (le slot reste occupé) et arrêt définitif (libère le slot).
// ============================================================================

const page = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '8',
  width: '100%',
  paddingTop: { base: '4', md: '8' },
});

const heroWrap = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: '5',
  paddingY: { base: '5', md: '10' },
});

const heroTitle = css({
  fontSize: 'h1',
  lineHeight: 'h1',
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-0.8px',
});

const heroHint = css({ fontSize: 'caption', color: 'text.tertiary' });

const listSection = css({ display: 'flex', flexDirection: 'column', gap: '4' });

const listTitle = css({
  fontSize: 'micro',
  color: 'text.tertiary',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  fontWeight: 'medium',
});

const row = css({
  display: 'flex',
  alignItems: 'center',
  gap: '5',
  paddingY: '4',
  paddingX: '5',
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'xl',
  cursor: 'pointer',
  transition: 'border-color 120ms ease, background 120ms ease',
  _hover: { borderColor: 'border.strong', bg: 'surface.subtle' },
});

const rowThumb = css({
  width: '44px',
  height: '44px',
  borderRadius: 'md',
  objectFit: 'cover',
  bg: 'surface.sunken',
  flexShrink: 0,
});

const rowThumbFallback = css({
  width: '44px',
  height: '44px',
  borderRadius: 'md',
  bg: 'surface.sunken',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
});

const rowMain = css({ display: 'flex', flexDirection: 'column', gap: '1', flex: 1, minWidth: 0 });

const rowName = css({
  fontSize: 'bodySm',
  fontWeight: 'semibold',
  color: 'text.primary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const rowMeta = css({ fontSize: 'caption', color: 'text.tertiary' });

const typeChip = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1.5',
    paddingY: '1',
    paddingX: '3',
    borderRadius: 'full',
    fontSize: 'caption',
    fontWeight: 'medium',
    borderWidth: '1px',
    borderStyle: 'solid',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  variants: {
    kind: {
      product: { bg: 'info.soft', color: 'info.default', borderColor: 'info.border' },
      vendor: { bg: 'success.soft', color: 'success.default', borderColor: 'success.border' },
    },
  },
});

const emptyState = css({
  paddingY: '16',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '3',
  color: 'text.tertiary',
  fontSize: 'bodySm',
  textAlign: 'center',
});

export const VeillePage: FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  let messageApi: typeof messageStatic;
  try {
    messageApi = App.useApp().message as unknown as typeof messageStatic;
  } catch {
    messageApi = messageStatic;
  }

  const { veilles, isLoading } = useUserVeilles();
  const quotas = useVeilleQuotas();
  const { pause, resume, stop } = useVeilleLifecycle();
  const { createVeille } = useCreateVeille();
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);

  const formatDate = (value: string): string =>
    new Date(value).toLocaleDateString(i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR');

  // Veille produit : la popup de critères complète, réutilisée telle quelle
  const handleProductSubmit = async (input: SearchModalInput) => {
    if (!input.criteria) throw new Error(t('productSearch.createError'));
    const result = await createVeille({ name: input.title, criteria: input.criteria });
    if (!result.success) throw new Error(result.error);
    navigate(`/veille/${result.veilleId}`);
  };

  const handleVendorSubmit = async (name: string, sellerVintedId: string) => {
    const result = await createVeille({ name, sellerVintedId });
    if (!result.success) {
      messageApi.error(result.error);
      return;
    }
    navigate(`/veille/${result.veilleId}`);
  };

  return (
    <AppLayout
      title={t('veille.title')}
      headerStats={
        quotas ? <span>{t('veille.slots', { used: quotas.veillesUsed, max: quotas.veillesMax })}</span> : undefined
      }
    >
      <div className={page}>
        <div className={heroWrap}>
          <h1 className={heroTitle}>{t('veille.heroTitle')}</h1>
          <div className={css({ display: 'flex', gap: '4', flexWrap: 'wrap', justifyContent: 'center' })}>
            <Button
              variant="primary"
              size="lg"
              icon={<Package size={15} strokeWidth={1.75} />}
              onClick={() => setIsProductModalOpen(true)}
            >
              {t('veille.newProduct')}
            </Button>
            <Button
              variant="secondary"
              size="lg"
              icon={<Store size={15} strokeWidth={1.75} />}
              onClick={() => setIsVendorModalOpen(true)}
            >
              {t('veille.newVendor')}
            </Button>
          </div>
          <span className={heroHint}>{t('veille.heroHint')}</span>
        </div>

        <div className={listSection}>
          <span className={listTitle}>{t('veille.listTitle')}</span>

          {isLoading ? (
            <div className={css({ display: 'flex', justifyContent: 'center', paddingY: '12' })}>
              <Spinner size={22} />
            </div>
          ) : veilles.length === 0 ? (
            <div className={emptyState}>
              <Radar size={28} strokeWidth={1.25} />
              <span>{t('veille.emptyTitle')}</span>
              <span className={css({ fontSize: 'caption' })}>{t('veille.emptyHint')}</span>
            </div>
          ) : (
            veilles.map((veille) => (
              <div
                key={veille.id}
                className={row}
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/veille/${veille.id}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') navigate(`/veille/${veille.id}`);
                }}
              >
                {veille.imageUrl ? (
                  <img src={veille.imageUrl} alt="" className={rowThumb} />
                ) : (
                  <span className={rowThumbFallback}>
                    <Radar size={16} strokeWidth={1.75} />
                  </span>
                )}
                <span className={rowMain}>
                  <span className={rowName}>{veille.name}</span>
                  <span className={rowMeta}>
                    {t('veille.watchedSince', { date: formatDate(veille.createdAt) })}
                    {veille.historyState === 'queued' || veille.historyState === 'running'
                      ? ` · ${t('veille.historyBuilding')}`
                      : ''}
                  </span>
                </span>
                <span className={typeChip({ kind: veille.type })}>
                  {veille.type === 'product' ? <Package size={11} strokeWidth={1.75} /> : <Store size={11} strokeWidth={1.75} />}
                  {veille.type === 'product' ? t('veille.typeProduct') : t('veille.typeVendor')}
                </span>
                {veille.state === 'paused' ? (
                  <span className={pausedBadge}>
                    <Pause size={10} strokeWidth={2} />
                    {t('veille.pausedBadge')}
                  </span>
                ) : (
                  <span className={liveDot} aria-label={t('veille.liveBadge')} />
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  icon={
                    veille.state === 'paused' ? (
                      <Play size={13} strokeWidth={1.75} />
                    ) : (
                      <Pause size={13} strokeWidth={1.75} />
                    )
                  }
                  handleClick={async (event) => {
                    event.stopPropagation();
                    await (veille.state === 'paused' ? resume(veille.id) : pause(veille.id));
                  }}
                  aria-label={veille.state === 'paused' ? t('veille.resume') : t('veille.pause')}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Trash2 size={13} strokeWidth={1.75} />}
                  handleClick={async (event) => {
                    event.stopPropagation();
                    if (window.confirm(t('veille.deleteConfirm', { name: veille.name }))) {
                      await stop(veille.id);
                    }
                  }}
                  aria-label={t('veille.delete')}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {/* Veille produit : mêmes critères que la recherche produit */}
      <SearchModal
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        onSubmit={handleProductSubmit}
        variant="veille"
      />

      <CreateVendorVeilleModal
        isOpen={isVendorModalOpen}
        onClose={() => setIsVendorModalOpen(false)}
        onSubmit={handleVendorSubmit}
      />
    </AppLayout>
  );
};
